import { Box, Stack, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import _ from 'lodash';
import { SupportType, pendingRequestsTypes, DUPLICATE_HIGHLIGHT_TYPES } from 'constants/requests.constants';
import { TableParagraph } from 'theme/elements/Paragraphs';
import { ServerPaginatedTable } from 'components/table';
import * as TableConfig from 'sections/pendingRequests/tablesConfig';
import { tableColumns } from 'sections/pendingRequests/tables.types';
import { dataType, Order } from 'components/table/types';
import { selectTranslations } from 'state/reducers/translation.reducers';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { pagesEnum } from 'constants/ui.constants';
import { getUserDetails } from 'state/reducers/user.reducers';
import { DUPLICATE_ROW_COLORS, mapingFeatureToEndpoint } from 'constants/pending-requests.constants';
import { customerInitialAction } from 'constants/customer-account.constants';
import { getCompanies } from 'state/reducers/global.reducer';
import { getDuplicateColorMap } from 'state/reducers/pending-requests.reducer';
import { verifyCloseAccountInline } from 'services/customer-accounts.service';
import { updateDigitalBalanceDocumentedInHyper } from 'services/pending-requests.service';
import { useConfirmationDialog } from 'hooks/shared/useConfirmationDialog';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { BalanceStatus } from 'constants/balance.constants';

type Props = {
	visibleTypes: pendingRequestsTypes[];
	pages: Record<string, number>;
	onPageChange: (type: string, page: number) => Promise<void>;
	onSortChange: (type: pendingRequestsTypes, sortBy: string, sortOrder: Order) => Promise<void> | void;
	perPage: number;
	onRefreshRequest?: (requestType: string, page: number) => void;
};

export const PendingRequestsTables: React.FC<Props> = ({
	visibleTypes,
	pages,
	onPageChange,
	onSortChange,
	perPage,
	onRefreshRequest,
}) => {
	const companies = useAppSelector(getCompanies);
	const t = useAppSelector(selectTranslations);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { permissions } = useAppSelector(getUserDetails);
	const { dialogConfig, openDialog, handleConfirm, handleCancel } = useConfirmationDialog();
	const sortableColumnsByType: Partial<Record<pendingRequestsTypes, string[]>> = {
		[pendingRequestsTypes.BALANCES]: ['createdAt', 'reference', 'amount'],
		[pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS]: [
			'createdAt',
			'feezbackUpdatedAt',
			'amount',
			'documentedOnHyperDate',
			'feezbackBankReferenceNumber',
		],
		[pendingRequestsTypes.BANK_ACCOUNTS]: [
			'createdAt',
			'reference',
			'accountOwnerName',
			'bankBranch',
			'accountNumber',
		],
		[pendingRequestsTypes.BENEFICIARIES]: [
			'createdAt',
			'updatedAt',
			'reference',
			'nickname',
			'companyName',
			'typeHebrew',
		],
		[pendingRequestsTypes.GENERAL_SUPPORT]: ['createdAt', 'reference', 'subject'],
		[pendingRequestsTypes.WITHDRAWAL_SUPPORT]: ['createdAt', 'reference', 'amount'],
		[pendingRequestsTypes.PAYMENTS]: ['createdAt', 'reference', 'amount', 'paymentType'],
		[pendingRequestsTypes.BALANCES_BO_REQUESTS]: ['createdAt', 'reference', 'amount', 'balanceType'],
		[pendingRequestsTypes.WITHDRAWAL_BO_APPROVED]: ['createdAt', 'reference', 'amount', 'status'],
	};
	const sortFieldMapByType: Partial<Record<pendingRequestsTypes, Record<string, string>>> = {
		[pendingRequestsTypes.BALANCES]: { createdAt: 'created_at', reference: 'reference', amount: 'amount' },
		[pendingRequestsTypes.BANK_ACCOUNTS]: {
			createdAt: 'created_at',
			reference: 'reference',
			accountOwnerName: 'accountOwnerName',
			bankBranch: 'bankBranch',
			accountNumber: 'accountNumber',
		},
		[pendingRequestsTypes.BENEFICIARIES]: {
			createdAt: 'created_at',
			updatedAt: 'updated_at',
			reference: 'reference',
			nickname: 'nickname',
			companyName: 'companyName',
			typeHebrew: 'type',
		},
		[pendingRequestsTypes.GENERAL_SUPPORT]: { createdAt: 'created_at', reference: 'reference', subject: 'subject' },
		[pendingRequestsTypes.WITHDRAWAL_SUPPORT]: {
			createdAt: 'created_at',
			reference: 'reference',
			amount: 'amount',
		},
		[pendingRequestsTypes.PAYMENTS]: {
			createdAt: 'created_at',
			reference: 'reference',
			amount: 'amount',
			paymentType: 'type',
		},
		[pendingRequestsTypes.BALANCES_BO_REQUESTS]: {
			createdAt: 'created_at',
			reference: 'reference',
			amount: 'amount',
			balanceType: 'type',
		},
		[pendingRequestsTypes.WITHDRAWAL_BO_APPROVED]: {
			createdAt: 'created_at',
			reference: 'reference',
			amount: 'amount',
			status: 'status',
		},
		[pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS]: {
			createdAt: 'created_at',
			feezbackUpdatedAt: 'feezbackUpdatedAt',
			amount: 'amount',
			documentedOnHyperDate: 'hyperDocumentedAt',
			feezbackBankReferenceNumber: 'feezbackBankReferenceNumber',
		},
	};
	const getDefaultSortBy = (type: pendingRequestsTypes) =>
		type === pendingRequestsTypes.BENEFICIARIES ? 'updatedAt' : 'createdAt';

	// Get all paginated requests data at once (hooks must be called at top level)
	const allPaginatedRequests = useAppSelector((state) => state.pendingRequests.paginatedRequests);
	const duplicateColorMap = useAppSelector(getDuplicateColorMap);

	const getDuplicateRowStyle = (row: dataType) => {
		if (!row.hasDuplicate) return undefined;
		const color = duplicateColorMap[row.companyId];
		if (!color) return undefined;

		return {
			backgroundColor: color,
			'&:hover': {
				backgroundColor: `${color} !important`,
				boxShadow: 'inset 0 0 0 9999px rgba(0,0,0,0.06)',
			},
		};
	};

	const handleDocumentedActionClick = (requestId: number) => {
		openDialog({
			title: t('page.digitalBalanceRequests.popup.title'),
			message: t('page.digitalBalanceRequests.popup.note'),
			confirmLabel: t('actions.name.yes'),
			cancelLabel: t('actions.name.no'),
			onConfirm: async () => {
				try {
					await dispatch(updateDigitalBalanceDocumentedInHyper(requestId));
					onRefreshRequest?.(pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS, pages[pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS] ?? 0);
				} catch (error) {
					console.error('Failed to update documented status:', error);
				}
			},
		});
	};

	// Build row action based on permissions
	const buildRowAction = (type: pendingRequestsTypes) => {
		const permission = 'read_' + _.get(mapingFeatureToEndpoint, type);
		if (!permissions?.permissions || !permission || !permissions.permissions.includes(permission)) {
			return {};
		}

		return {
			rowAction: (row: dataType) => {
				if (
					[pendingRequestsTypes.WITHDRAWAL_SUPPORT].includes(type) &&
					row.type === SupportType.WithdrawalWithClose
				) {
					if (row?.activityBalanceAddition?.status === BalanceStatus.Rejected) return;

					dispatch(verifyCloseAccountInline(row?.companyId))
						.unwrap()
						.then((res) => {
							if (
								!_.get(res, 'isCompanyHasPendingFinancialRequest') &&
								!_.get(res, 'isCompanyHasBalance')
							) {
								navigate(
									'/' +
										`${pagesEnum.CUSTOMER_ACCOUNTS}/` +
										`${row.companyId}/` +
										`${customerInitialAction.CLOSE_ACCOUNT}`
								);
							} else {
								navigate(`/${pagesEnum.COMPANY_FEATURE_APPROVE}/${type}/${row.id}`);
							}
						});
				} else if (type === pendingRequestsTypes.GENERAL_SUPPORT) {
					navigate(
						`/${pagesEnum.CUSTOMER_ACCOUNTS}/${row.companyId}/${customerInitialAction.SUPPORT_REQUESTS}`
					);
				} else if (type === pendingRequestsTypes.BALANCES_BO_REQUESTS) {
					navigate(`/${pagesEnum.COMPANY_FEATURE_PROFILE}/${type}/${row.id}`);
				} else {
					navigate(`/${pagesEnum.COMPANY_FEATURE_APPROVE}/${type}/${row.id}`);
				}
			},
		};
	};

	const showDuplicateLegend =
		visibleTypes.includes(pendingRequestsTypes.BALANCES) ||
		visibleTypes.includes(pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS);

	return (
		<Stack spacing={4}>
			{showDuplicateLegend && (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1.5,
						px: 1,
						py: 1.5,
						bgcolor: 'action.hover',
						borderRadius: 1,
					}}
				>
					<Box
						sx={{
							width: 20,
							height: 20,
							borderRadius: 0.5,
							bgcolor: DUPLICATE_ROW_COLORS[0],
							border: '1px solid',
							borderColor: 'divider',
						}}
					/>
					<Typography variant="body2" color="text.secondary">
						{t('page.pendingRequests.duplicateLegend')}
					</Typography>
				</Box>
			)}
			{visibleTypes.map((type) => {
				// Get paginated data for this type from the state
				const requestData = allPaginatedRequests?.[type] || {
					data: [],
					pagination: { total: 0, hasMore: false },
					loading: false,
				};

			const sortableColumns = sortableColumnsByType[type] || [];
			const mapSortBy = (sortBy: string) => sortFieldMapByType[type]?.[sortBy];
			const getRowStyle = DUPLICATE_HIGHLIGHT_TYPES.includes(type) ? getDuplicateRowStyle : undefined;

				return (
					<Stack key={type} spacing={2}>
						<TableParagraph
							title={t('page.pendingRequests.table.' + type + '.title')}
							text={requestData.pagination.total + ' ' + t('messages.requests')}
						/>
						<ServerPaginatedTable
							{...buildRowAction(type)}
							defaultSortBy={getDefaultSortBy(type)}
							defaultOrderBy="desc"
							data={TableConfig.tableData(type, Array.isArray(requestData.data) ? requestData.data : [], t, companies)}
							getRowStyle={getRowStyle}
						columns={TableConfig.buildColumns(
							type,
							t,
							sortableColumns,
							type === pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS ? handleDocumentedActionClick : undefined
						)}
							perPage={perPage}
							serverPagination={{
								total: requestData.pagination.total,
								hasMore: requestData.pagination.hasMore,
								currentPage: pages[type] || 0,
								onPageChange: (page) => onPageChange(type, page),
								onSortChange: (sortBy, sortOrder) => {
									const mappedSortBy = mapSortBy(sortBy);
									if (!mappedSortBy) return;
									return onSortChange(type, mappedSortBy, sortOrder);
								},
								isLoading: requestData.loading,
							}}
						/>
					</Stack>
				);
			})}
			<ConfirmationDialog
				open={dialogConfig.open}
				title={dialogConfig.title}
				message={dialogConfig.message}
				confirmLabel={dialogConfig.confirmLabel}
				cancelLabel={dialogConfig.cancelLabel}
				onConfirm={handleConfirm}
				onCancel={handleCancel}
			/>
		</Stack>
	);
};
